/**
 * Uppsägning av lokalhyresavtal för avflyttning (12 kap. JB).
 *
 * Sibling of `uppsagning_villkorsandring_lokal`: same Parter / Uppsägning /
 * signing points (reused from that family), but the Meddelande states the
 * besittningsbrytande grund(er) under 12 kap. 57 § JB instead of Alt 1/Alt 2.
 *
 * Legal text verbatim from `base-templates/R-20_Uppsägning_avflyttning_lokal.docx`
 * (Haymanot-approved). DO NOT paraphrase or reorder.
 */
import { option, optionList, p, section, t } from '@hyranai/regta-document-ir';
import { optionChosen, shown, textVal, ticked, toSlotOptions, whenIncludes } from '../common/index.js';
import { letterOpening } from '../andring_hyresvillkor/kit.js';
import { terminationLine } from '../uppsagning_villkorsandring_lokal/blocks.js';
const LEGAL = {
    plane: 'legal',
    status: 'published',
    language: 'sv-SE',
    legalReview: { reviewedBy: 'Haymanot', source: 'base-templates/R-20_Uppsägning_avflyttning_lokal.docx' },
};
/**
 * Besittningsbrytande grunder (12 kap. 57 § JB). `renderLabel` is the
 * verbatim clause printed in the document; `label` is the editor's short
 * name; `detailKey` the dependent free-text slot.
 */
export const GROUND_OPTIONS = [
    {
        code: 'misskotsel',
        label: 'Åsidosatt förpliktelser (misskötsel)',
        summary: 'Tenant has neglected obligations so seriously that extension cannot reasonably be required.',
        renderLabel: 'Som hyresgäst har du åsidosatt dina förpliktelser i så hög grad att det inte skäligen kan fordras att hyresförhållandet förlängs:',
        detailKey: 'ground_misskotsel_desc',
        detailRequired: true,
    },
    {
        code: 'rivning',
        label: 'Rivning + ersättningslokal',
        summary: 'The building is to be demolished and the landlord offers an acceptable replacement premises.',
        renderLabel: 'Huset ska rivas och hyresvärden anvisar därför följande godtagbar(-a) ersättningslokal(-er):',
        detailKey: 'ground_rivning_lokaler',
    },
    {
        code: 'ombyggnad',
        label: 'Ombyggnad + ersättningslokal',
        summary: 'The building is to undergo major rebuilding and the landlord offers an acceptable replacement premises.',
        renderLabel: 'Huset ska genomgå en sådan ombyggnad att du som hyresgäst uppenbarligen inte kan sitta kvar i lokalen utan nämnvärd olägenhet för genomförandet av ombyggnaden. Hyresvärden anvisar följande godtagbar(-a) ersättningslokal(-er):',
        detailKey: 'ground_ombyggnad_lokaler',
    },
    {
        code: 'ombyggnad_avbojt',
        label: 'Ombyggnad — avböjd ersättningslokal',
        summary: 'Major rebuilding, but the tenant declined an acceptable replacement premises without valid reason.',
        renderLabel: 'Huset ska genomgå en sådan ombyggnad att du som hyresgäst uppenbarligen inte kan sitta kvar i lokalen utan nämnvärd olägenhet för genomförandet av ombyggnaden, men att du som hyresgäst utan befogad anledning avböjt en godtagbar ersättningslokal som hyresvärden har anvisat.',
    },
    {
        code: 'befogad_anledning',
        label: 'Befogad anledning (lokalen behövs)',
        summary: 'The landlord has justified cause to end the tenancy because the premises are needed for a stated use.',
        renderLabel: 'Hyresvärden har befogad anledning att upplösa hyresförhållandet eftersom lokalen behövs för:',
        detailKey: 'ground_befogad_desc',
        detailRequired: true,
    },
    {
        code: 'sakligt_skal',
        label: 'Sakligt skäl (fritext)',
        summary: 'The landlord has another objectively valid reason for the termination (free text).',
        renderLabel: 'Hyresvärden har sakligt skäl för uppsägningen, nämligen:',
        detailKey: 'ground_sakligt_desc',
        detailRequired: true,
    },
];
/** Editor label / help of each ground's dependent free-text slot. */
const GROUND_DETAIL_SLOTS = {
    misskotsel: { label: 'Åsidosatt förpliktelser — beskrivning' },
    rivning: { label: 'Rivning — ersättningslokal(-er)', help: 'En lokal per rad. Lägg till fler vid behov.' },
    ombyggnad: { label: 'Ombyggnad — ersättningslokal(-er)', help: 'En lokal per rad. Lägg till fler vid behov.' },
    befogad_anledning: {
        label: 'Befogad anledning — skäl',
        help: 'T.ex. egen verksamhet, omdisponering eller omstrukturering av fastighetens lokalbestånd.',
    },
    sakligt_skal: { label: 'Sakligt skäl' },
};
export const rubrik = {
    ...LEGAL,
    blockKey: 'uppsagning_avflyttning_lokal.rubrik',
    version: 'v1',
    title: 'Rubrik',
    summary: 'The document title above the first point.',
    slots: [],
    compile: () => letterOpening({ title: 'Uppsägning av lokalhyresavtal för avflyttning' }),
};
export const meddelande = {
    ...LEGAL,
    blockKey: 'uppsagning_avflyttning_lokal.meddelande',
    version: 'v1',
    title: 'Meddelande',
    summary: 'The termination for vacating, the ground(s) for refusing extension (12 kap. 57 § JB), and the 58 § medling notice.',
    slots: [
        {
            key: 'grounds',
            label: 'Anledning till att förlängning vägras',
            type: 'multi_select',
            required: true,
            options: toSlotOptions(GROUND_OPTIONS),
            help: '"Ombyggnad — avböjd ersättningslokal" används endast om hyresgästen redan anvisats en godtagbar lokal.',
        },
        ...GROUND_OPTIONS.flatMap((g) => {
            const detail = GROUND_DETAIL_SLOTS[g.code];
            if (!detail || !g.detailKey)
                return [];
            const when = whenIncludes('grounds', g);
            return [{ key: g.detailKey, type: 'textarea', ...detail, visibleWhen: when, ...(g.detailRequired ? { requiredWhen: when } : {}) }];
        }),
        {
            key: 'delgivning_note',
            label: 'Observera – delgivning',
            type: 'note',
            help: 'Uppsägningen måste vara delgiven hyresgästen senast sista dagen för uppsägning. Säkerställ att delgivning kan styrkas.',
        },
    ],
    compile: (input, ctx) => {
        const chosen = (g) => optionChosen(GROUND_OPTIONS, input.grounds, g);
        return {
            nodes: [
                section('meddelande', 'Meddelande', [
                    terminationLine('avflyttning', input, ctx),
                    p('Anledningen till att förlängning vägras är följande:'),
                    optionList(GROUND_OPTIONS.map((g) => {
                        const item = option(ticked(ctx, chosen(g)), [t(g.renderLabel)], { code: g.code });
                        return g.detailKey && shown(ctx, chosen(g)) ? { ...item, children: [p([textVal(input, g.detailKey, 'full')])] } : item;
                    }), { sourceKey: 'grounds', select: 'multi' }),
                    p('Om ni inte accepterar att hyresförhållandet upphör har ni att inom två månader från denna uppsägning hänskjuta tvisten till hyresnämnden för medling enligt 12 kap. 58 § jordabalken. I annat fall förfaller rätten till ersättning med anledning av uppsägningen.'),
                ]),
            ],
        };
    },
};
export const UPPSAGNING_AVFLYTTNING_LOKAL_BLOCKS = [rubrik, meddelande];
//# sourceMappingURL=blocks.js.map